/** Sentinel values for AI Warden react-select scope filters. */
export const SCOPE_SENTINEL = {
    ALL: '__ALL__',
    NONE: '__NONE__',
};

const AI_WARDEN_FONT = 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif';

export const SELECT_STYLES = {
    control: (provided, state) => ({
        ...provided,
        borderColor: state.isFocused ? '#b8c0dc' : '#d8deef',
        borderRadius: '10px',
        minHeight: '36px',
        fontSize: 13,
        fontFamily: AI_WARDEN_FONT,
        backgroundColor: '#fafbff !important',
        boxShadow: state.isFocused ? '0 0 0 1px rgba(90, 111, 212, 0.25)' : 'none',
    }),
    valueContainer: provided => ({
        ...provided,
        minHeight: '34px',
        padding: '2px 8px',
        gap: 4,
    }),
    input: provided => ({
        ...provided,
        margin: 0,
        fontFamily: AI_WARDEN_FONT,
        fontSize: 13,
    }),
    placeholder: provided => ({
        ...provided,
        fontFamily: AI_WARDEN_FONT,
        fontSize: 13,
        color: '#8b93ad',
    }),
    singleValue: provided => ({
        ...provided,
        fontFamily: AI_WARDEN_FONT,
        fontSize: 13,
        color: 'var(--Brand-Primary, #1e2433)',
    }),
    multiValue: provided => ({
        ...provided,
        backgroundColor: '#e8ecf4',
        borderRadius: 6,
        margin: '2px',
    }),
    multiValueLabel: provided => ({
        ...provided,
        fontFamily: AI_WARDEN_FONT,
        fontSize: 12,
        fontWeight: 500,
        color: '#3d4663',
        padding: '2px 4px',
    }),
    multiValueRemove: provided => ({
        ...provided,
        color: '#6b7394',
        borderRadius: '0 6px 6px 0',
        ':hover': {
            backgroundColor: '#d8deef',
            color: '#3d4663',
        },
    }),
    menu: provided => ({
        ...provided,
        fontFamily: AI_WARDEN_FONT,
        fontSize: 13,
        zIndex: 20,
    }),
    option: (provided, state) => ({
        ...provided,
        fontFamily: AI_WARDEN_FONT,
        fontSize: 13,
        backgroundColor: state.isSelected ? '#e8ecf4' : state.isFocused ? '#f4f6fb' : '#fff',
        color: '#3d4663',
    }),
    indicatorSeparator: () => ({
        display: 'none',
    }),
    indicatorsContainer: provided => ({
        ...provided,
        height: '34px',
    }),
};

export function workstreamOptionsFromList(workstreams = []) {
    return (workstreams || [])
        .filter(ws => ws?.id != null)
        .map(ws => ({
            label: ws.name || `Workstream ${ws.id}`,
            value: String(ws.id),
        }));
}

export function folderOptionsFromList(folders = []) {
    return [
        { label: 'All folders', value: SCOPE_SENTINEL.ALL },
        ...(folders || []).map(f => ({
            label: f.label || f.depth_path || f.name || `Folder ${f.id}`,
            value: String(f.id),
        })),
    ];
}

export function tagOptionsFromList(tags = [], { includeNone = true, includeAll = true } = {}) {
    const out = [];
    if (includeNone) out.push({ label: 'None', value: SCOPE_SENTINEL.NONE });
    if (includeAll) out.push({ label: 'All tags', value: SCOPE_SENTINEL.ALL });
    for (const tag of tags || []) {
        const label = String(tag).trim();
        if (label) out.push({ label, value: label });
    }
    return out;
}

export function selectedWorkstreamIds(selectedOptions = [], allWorkstreamIds = []) {
    const values = (selectedOptions || []).map(o => o?.value).filter(Boolean);
    if (!values.length || values.includes(SCOPE_SENTINEL.ALL)) {
        return allWorkstreamIds.map(String);
    }
    return values.filter(v => v !== SCOPE_SENTINEL.ALL);
}

export function buildWorkstreamIdsQuery(selectedOptions = [], allWorkstreamIds = []) {
    const ids = selectedWorkstreamIds(selectedOptions, allWorkstreamIds).map(Number).filter(Number.isFinite);
    if (!ids.length || ids.length >= allWorkstreamIds.length) {
        return null;
    }
    return ids;
}

export function deriveSourceFlags(selectedIrlOptions = [], selectedObsOptions = []) {
    const irlNone = selectedIrlOptions.some(o => o?.value === SCOPE_SENTINEL.NONE);
    const obsNone = selectedObsOptions.some(o => o?.value === SCOPE_SENTINEL.NONE);

    const irlTags = selectedIrlOptions.map(o => o?.value).filter(v => v && v !== SCOPE_SENTINEL.NONE && v !== SCOPE_SENTINEL.ALL);
    const obsTags = selectedObsOptions.map(o => o?.value).filter(v => v && v !== SCOPE_SENTINEL.NONE && v !== SCOPE_SENTINEL.ALL);

    return {
        includeIrl: !irlNone,
        includeObservations: !obsNone,
        irlTags: irlTags.length ? irlTags : undefined,
        observationTags: obsTags.length ? obsTags : undefined,
    };
}

export function selectedFolderIds(selectedFolderOptions = []) {
    const values = (selectedFolderOptions || []).map(o => o?.value).filter(Boolean);
    if (!values.length || values.includes(SCOPE_SENTINEL.ALL)) {
        return null;
    }
    return values.map(Number).filter(Number.isFinite);
}

export function collectDescendantFolderIdsFromFlat(folders = [], rootIds = []) {
    const byParent = new Map();
    for (const f of folders || []) {
        const parentKey = f.parent_id != null ? String(f.parent_id) : 'root';
        if (!byParent.has(parentKey)) byParent.set(parentKey, []);
        byParent.get(parentKey).push(Number(f.id));
    }
    const allowed = new Set(rootIds.map(Number).filter(Number.isFinite));
    const queue = [...allowed];
    while (queue.length) {
        const id = queue.pop();
        for (const childId of byParent.get(String(id)) || []) {
            if (!allowed.has(childId)) {
                allowed.add(childId);
                queue.push(childId);
            }
        }
    }
    return allowed;
}

export function handleExclusiveSentinelChange(selected, option, sentinels = []) {
    const value = option?.value;
    if (sentinels.includes(value)) {
        return [option];
    }
    return (selected || []).filter(o => !sentinels.includes(o?.value));
}

export function countScopeLightweightList(data, keys = []) {
    if (!data) return 0;
    if (Array.isArray(data)) return data.length;
    for (const key of keys) {
        if (Array.isArray(data[key])) return data[key].length;
    }
    return 0;
}

/** Count IRL rows in scope from GET …/ai_warden/{project}/irl/lightweight/ ({ irls: [...] }). */
export function countScopeIrlResponses(data) {
    if (Array.isArray(data?.irls)) {
        return data.irls.filter(row => row?.active !== false).length;
    }
    return countScopeLightweightList(data, ['irls', 'irl', 'items', 'results', 'responses']);
}

/** Summary label for selected workstreams in context pills (e.g. "3 workstreams"). */
export function formatWorkstreamContextSummary(count) {
    const n = Number(count) || 0;
    if (n <= 0) return null;
    return n === 1 ? '1 workstream' : `${n} workstreams`;
}
